/**
 * F-bank-direct-reasons (CEO 2026-10-23) — motivos PADRONIZADOS da conciliação
 * direta (sem documento) do módulo Banco.
 *
 * Antes o operador digitava um texto livre ("Locação", "Transferencia entre
 * empresas", "perssonel"), que saía como estava no relevé ao contador — em
 * português num documento francês. Agora ele escolhe a CATEGORIA (a mesma
 * lista suspensa da tela Extratos) e um MOTIVO desta lista; o Gestion guarda
 * a chave e imprime o rótulo no idioma do relevé (FR/PT/EN). O texto livre
 * continua existindo só como "complemento / referência" (nº de fatura, nome)
 * e não se traduz.
 *
 * Fonte ÚNICA: a API (export do relevé, validação) e o web (modal) importam
 * daqui. Chaves são estáveis — ficam gravadas em
 * `bank_transactions.reconciliation_reason`.
 */

/** As 16 categorias do enum `bank_transaction_category` (mesma ordem da tela). */
export const BANK_CATEGORIES = [
  "revenue",
  "supplier_payment",
  "tax",
  "salary",
  "bank_fee",
  "transfer",
  "other",
  "locations_charges_locatives",
  "primes_assurance",
  "deplacements_reception",
  "fournitures_consommables",
  "intermediaires_honoraires",
  "vat_payment",
  "personal",
  "partner_contribution",
  "partner_reimbursement",
] as const;
export type BankCategory = (typeof BANK_CATEGORIES)[number];

export type ReasonLang = "fr" | "pt" | "en";

export interface BankDirectReason {
  key: string;
  fr: string;
  pt: string;
  en: string;
  /** Categorias em que este motivo é sugerido primeiro (vazio = qualquer). */
  categories: readonly BankCategory[];
}

export const BANK_DIRECT_REASONS = [
  // ── Banco ──
  {
    key: "bank_subscription",
    fr: "Cotisation / abonnement bancaire",
    pt: "Cotisação / assinatura bancária",
    en: "Bank subscription / fees",
    categories: ["bank_fee"],
  },
  {
    key: "loan_installment",
    fr: "Échéance de prêt",
    pt: "Parcela de empréstimo",
    en: "Loan installment",
    categories: ["bank_fee", "other"],
  },
  {
    key: "cashback",
    fr: "Cashback / avantage commercial",
    pt: "Cashback / vantagem comercial",
    en: "Cashback / commercial rebate",
    categories: ["bank_fee", "revenue", "other"],
  },
  // ── Pessoal / sócio ──
  {
    key: "payroll",
    fr: "Paie / charges sociales",
    pt: "Folha / encargos sociais",
    en: "Payroll / social charges",
    categories: ["salary"],
  },
  {
    key: "partner_loan_repayment",
    fr: "Remboursement d'emprunt à l'associé",
    pt: "Reembolso de empréstimo ao sócio",
    en: "Partner loan repayment",
    categories: ["partner_reimbursement"],
  },
  {
    key: "personal_expense",
    fr: "Dépense personnelle de l'associé",
    pt: "Despesa pessoal do sócio",
    en: "Partner's personal expense",
    categories: ["personal", "partner_reimbursement"],
  },
  {
    key: "partner_loan",
    fr: "Apport / prêt de l'associé",
    pt: "Aporte / empréstimo do sócio",
    en: "Partner contribution / loan",
    categories: ["partner_contribution"],
  },
  // ── Veículo / deslocamento / locação ──
  {
    key: "vehicle_expense",
    fr: "Frais de véhicule (carburant, péage, entretien)",
    pt: "Despesas com veículo (combustível, pedágio, manutenção)",
    en: "Vehicle expenses (fuel, tolls, maintenance)",
    categories: ["deplacements_reception"],
  },
  {
    key: "travel",
    fr: "Déplacement / hébergement / repas",
    pt: "Deslocamento / hospedagem / refeições",
    en: "Travel / lodging / meals",
    categories: ["deplacements_reception"],
  },
  {
    key: "vehicle_rental",
    fr: "Location de véhicule",
    pt: "Locação de veículo",
    en: "Vehicle rental",
    categories: ["locations_charges_locatives"],
  },
  {
    key: "rent",
    fr: "Loyer / charges locatives",
    pt: "Aluguel / encargos locativos",
    en: "Rent / rental charges",
    categories: ["locations_charges_locatives"],
  },
  // ── Fornecedores / serviços ──
  {
    key: "insurance",
    fr: "Assurance",
    pt: "Seguro",
    en: "Insurance",
    categories: ["primes_assurance"],
  },
  {
    key: "software_subscription",
    fr: "Abonnement logiciel / services en ligne",
    pt: "Assinatura de software / serviços online",
    en: "Software / online services subscription",
    categories: ["supplier_payment"],
  },
  {
    key: "advertising",
    fr: "Publicité (Meta, Google)",
    pt: "Publicidade (Meta, Google)",
    en: "Advertising (Meta, Google)",
    categories: ["supplier_payment"],
  },
  {
    key: "supplies",
    fr: "Fournitures / consommables",
    pt: "Materiais / consumíveis",
    en: "Supplies / consumables",
    categories: ["fournitures_consommables", "supplier_payment"],
  },
  {
    key: "accounting_fees",
    fr: "Comptabilité / expert-comptable",
    pt: "Contabilidade",
    en: "Accounting",
    categories: ["intermediaires_honoraires"],
  },
  {
    key: "professional_fees",
    fr: "Honoraires (avocat, conseil)",
    pt: "Honorários (advogado, consultor)",
    en: "Professional fees (lawyer, consultant)",
    categories: ["intermediaires_honoraires"],
  },
  {
    key: "technician_payment_direct",
    fr: "Paiement d'un technicien (sans document lié)",
    pt: "Pagamento de técnico (sem documento ligado)",
    en: "Technician payment (no linked document)",
    categories: ["supplier_payment"],
  },
  {
    key: "supplier_service",
    fr: "Prestation / service d'un fournisseur",
    pt: "Serviço de fornecedor",
    en: "Supplier service",
    categories: ["supplier_payment"],
  },
  {
    key: "invoice_payment",
    fr: "Paiement d'une facture (n° en complément)",
    pt: "Pagamento de fatura (nº no complemento)",
    en: "Invoice payment (number in the complement)",
    categories: ["supplier_payment", "transfer"],
  },
  {
    key: "supplier_refund",
    fr: "Remboursement / avoir d'un fournisseur",
    pt: "Estorno / avoir de fornecedor",
    en: "Supplier refund / credit note",
    categories: ["supplier_payment", "other"],
  },
  // ── Transferências ──
  {
    key: "intercompany_transfer",
    fr: "Virement entre sociétés du groupe",
    pt: "Transferência entre empresas do grupo",
    en: "Intercompany transfer",
    categories: ["transfer"],
  },
  {
    key: "internal_transfer",
    fr: "Virement entre comptes de la société",
    pt: "Transferência entre contas da empresa",
    en: "Transfer between own accounts",
    categories: ["transfer"],
  },
  {
    key: "currency_exchange",
    fr: "Change de devises",
    pt: "Câmbio de moeda",
    en: "Currency exchange",
    categories: ["transfer", "other"],
  },
  // ── Receitas ──
  {
    key: "customer_receipt_direct",
    fr: "Encaissement client (sans facture liée)",
    pt: "Recebimento de cliente (sem fatura ligada)",
    en: "Customer receipt (no linked invoice)",
    categories: ["revenue"],
  },
  {
    key: "misc_revenue",
    fr: "Recette diverse",
    pt: "Receita diversa",
    en: "Miscellaneous revenue",
    categories: ["revenue", "other"],
  },
  // ── Impostos ──
  {
    key: "tax_current",
    fr: "Impôt / taxe de l'exercice (DGFiP, URSSAF)",
    pt: "Imposto / taxa do exercício (DGFiP, URSSAF)",
    en: "Current-year tax (DGFiP, URSSAF)",
    categories: ["tax"],
  },
  {
    key: "vat_payment",
    fr: "TVA reversée à l'État",
    pt: "TVA repassada ao Estado",
    en: "VAT paid to the State",
    categories: ["vat_payment", "tax"],
  },
  // ── Especial: fora do resultado do exercício (fiscal exclui) ──
  {
    key: "prior_year_settlement",
    fr: "Règlement d'une dette de l'exercice précédent",
    pt: "Liquidação de dívida do exercício anterior",
    en: "Prior-year debt settlement",
    categories: [],
  },
  {
    key: "other",
    fr: "Autre (préciser en complément)",
    pt: "Outro (especificar no complemento)",
    en: "Other (specify in the complement)",
    categories: ["other"],
  },
] as const satisfies readonly BankDirectReason[];

export type BankDirectReasonKey = (typeof BANK_DIRECT_REASONS)[number]["key"];

export const BANK_DIRECT_REASON_KEYS = BANK_DIRECT_REASONS.map((r) => r.key) as [
  BankDirectReasonKey,
  ...BankDirectReasonKey[],
];

/** Motivo que tira a operação do resultado do exercício (fiscal ignora). */
export const PRIOR_YEAR_REASON: BankDirectReasonKey = "prior_year_settlement";

export function isBankDirectReasonKey(v: unknown): v is BankDirectReasonKey {
  return typeof v === "string" && BANK_DIRECT_REASONS.some((r) => r.key === v);
}

/** Rótulo no idioma pedido (idiomas fora de fr/pt/en caem no francês). */
export function bankDirectReasonLabel(key: string, lang: string): string {
  const r = BANK_DIRECT_REASONS.find((x) => x.key === key);
  if (!r) return key;
  const l: ReasonLang = lang === "pt" || lang === "en" ? lang : "fr";
  return r[l];
}

/**
 * Motivos para o seletor: primeiro os sugeridos para a categoria, depois os
 * demais (o "prior_year_settlement" é sugerido em todas).
 */
export function bankDirectReasonsFor(category: BankCategory | null): {
  suggested: readonly BankDirectReason[];
  others: readonly BankDirectReason[];
} {
  const suggested = BANK_DIRECT_REASONS.filter(
    (r) =>
      r.key === PRIOR_YEAR_REASON ||
      (category != null && (r.categories as readonly string[]).includes(category)),
  );
  const others = BANK_DIRECT_REASONS.filter((r) => !suggested.includes(r));
  return { suggested, others };
}
